/**
 * AI Pair Programming Service
 * Watches the active file and proactively suggests improvements.
 */
import { apiUrl, authFetch } from '../lib/api'

export interface PairSuggestion {
  id: string
  type: 'bug' | 'refactor' | 'completion' | 'tip' | 'warning'
  message: string
  code?: string
  line?: number
  timestamp: number
}

type SuggestionCallback = (suggestion: PairSuggestion) => void

let enabled = false
let callback: SuggestionCallback | null = null
let lastCode = ''
let lastAnalyzedCode = ''
let debounceTimer: ReturnType<typeof setTimeout> | null = null
let analysisCount = 0

const DEBOUNCE_MS = 3000 // Wait 3 seconds after last edit before analyzing
const MAX_ANALYSES_PER_MINUTE = 5

export function startPairProgramming(cb: SuggestionCallback) {
  enabled = true
  callback = cb
  analysisCount = 0
  // Reset counter every minute
  setInterval(() => { analysisCount = 0 }, 60000)
}

export function stopPairProgramming() {
  enabled = false
  callback = null
  if (debounceTimer) clearTimeout(debounceTimer)
}

export function isPairActive(): boolean {
  return enabled
}

// Called by the editor whenever code changes
export function onCodeChange(code: string, language: string, filePath: string) {
  if (!enabled || !callback) return
  lastCode = code

  // Debounce — only analyze after user stops typing for 3 seconds
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    analyzeCode(code, language, filePath)
  }, DEBOUNCE_MS)
}

// Detect patterns that suggest the user is stuck
export function onUserPaused(code: string, language: string, filePath: string) {
  if (!enabled || !callback) return
  // If code hasn't changed much since last analysis, skip
  if (code === lastAnalyzedCode) return
  analyzeCode(code, language, filePath)
}

async function analyzeCode(code: string, language: string, filePath: string) {
  if (!enabled || !callback) return
  if (analysisCount >= MAX_ANALYSES_PER_MINUTE) return
  if (code === lastAnalyzedCode) return
  if (code.length < 10) return

  lastAnalyzedCode = code
  analysisCount++

  try {
    const res = await authFetch(apiUrl('/api/ai-tools/pair'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, language, filePath }),
    })

    if (!res.ok) return
    const data = await res.json()

    if (data.suggestions && Array.isArray(data.suggestions)) {
      for (const s of data.suggestions) {
        callback({
          id: crypto.randomUUID(),
          type: s.type ?? 'tip',
          message: s.message,
          code: s.code,
          line: s.line,
          timestamp: Date.now(),
        })
      }
    }
  } catch {}
}
